import { Component, inject, Inject, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { Bank } from "@models/bank";
import { BankAccount } from "@models/bank-account";
import {
  NgbTypeaheadModule,
  NgbTypeaheadSelectItemEvent,
} from "@ng-bootstrap/ng-bootstrap";
import { AutoCompleteModule } from "primeng/autocomplete";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { DialogHeaderComponent } from "../../../../shared/components/dialog-header/dialog-header.component";
import { HttpErrorResponse } from "@angular/common/http";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  finalize,
  Observable,
  of,
  OperatorFunction,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs";
import { SnackBarService } from "../../../services/snack-bar.service";
import { WalletService } from "../../../services/wallet.service";

export interface EditBankAccountDialogData {
  bankAccount: BankAccount | null;
}

@Component({
  selector: "app-edit-bank-account-dialog",
  imports: [
    ReactiveFormsModule,
    AutoCompleteModule,
    NgbTypeaheadModule,
    MatDialogModule,
    DialogHeaderComponent,
    ButtonComponent,
  ],
  templateUrl: "./edit-bank-account-dialog.component.html",
  styleUrl: "./edit-bank-account-dialog.component.scss",
})
export class EditBankAccountDialogComponent implements OnInit {
  private readonly _fb = inject(FormBuilder);
  private readonly _snackbarService = inject(SnackBarService);
  private readonly _walletService = inject(WalletService);
  readonly dialogRef = inject(MatDialogRef<EditBankAccountDialogComponent>);

  selectedCountry = "";

  form = this._fb.group({
    bank_name: new FormControl<string | null | Bank>("", [
      Validators.required,
      Validators.maxLength(100),
    ]),
    bank_code: [
      "",
      [Validators.required, Validators.minLength(3), Validators.maxLength(5)],
    ],
    agency: ["", [Validators.required, Validators.maxLength(10)]],
    account_number: ["", [Validators.required, Validators.maxLength(10)]],
    account_digit: ["", [Validators.required, Validators.maxLength(1)]],
    account_holder: ["", [Validators.required, Validators.maxLength(100)]],
    pix_key: ["", [Validators.required, Validators.maxLength(254)]],
  });

  bankAccount: BankAccount | null;

  searching = false;
  searchFailed = false;
  submitting = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: EditBankAccountDialogData) {
    this.bankAccount = data.bankAccount;
  }

  controls = this.form.controls;

  banks$ = this._walletService.getBanks().pipe(shareReplay());

  banks: OperatorFunction<string, readonly Bank[]> = (
    text$: Observable<string>,
  ) =>
    text$.pipe(
      tap((val) => console.log(val)),
      withLatestFrom(this.banks$),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(([searchTerm, banks]) => {
        return of(
          banks.filter((bank) => {
            const bankName = bank.fullName || bank.name;
            return bankName.toLowerCase()?.includes(searchTerm.toLowerCase());
          }),
        );
      }),
    );

  ngOnInit(): void {
    if (this.bankAccount) {
      this.form.patchValue({
        ...this.bankAccount,
      });

      this.form.controls.bank_code.disable();
    }
    this.form.controls.bank_name.setValue(this.bankAccount?.bank_name ?? null);
  }

  formatter = (x: { fullName: string } | string) =>
    typeof x === "object" ? x.fullName : x;

  confirm() {
    this.form.markAllAsTouched();

    if (this.form.invalid || this.submitting) return;

    const bankName = this.form.controls.bank_name.value as Bank | string;
    const bankInfo = {
      ...this.bankAccount,
      ...this.form.getRawValue(),
      bank_name: typeof bankName === "object" ? bankName.name : bankName,
    } as BankAccount;

    const request$ = this.bankAccount?.id
      ? this._walletService.updateBankAccount(bankInfo)
      : this._walletService.createBankAccount(bankInfo);

    this.submitting = true;
    request$
      .pipe(
        take(1),
        finalize(() => (this.submitting = false)),
        tap((res) => this.dialogRef.close(res)),
        catchError((error: HttpErrorResponse) => {
          this._snackbarService.openSnackBar(
            this.getBankAccountErrorMessage(error),
            "Fechar",
            "error",
          );
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private getBankAccountErrorMessage(error: HttpErrorResponse): string {
    const raw = error?.error;
    const message = Array.isArray(raw)
      ? raw[0]
      : (raw?.error ?? raw?.message ?? raw);

    if (typeof message !== "string") {
      return "Não foi possível salvar os dados bancários. Tente novamente.";
    }

    const tooShort = message.match(
      /is too short \(minimum is (\d+) characters?\)/i,
    );
    if (tooShort) {
      return `O código do banco é muito curto (mínimo de ${tooShort[1]} caracteres).`;
    }

    return message;
  }

  handleBankSelect(selectEvent: NgbTypeaheadSelectItemEvent<Bank>) {
    this.form.controls.bank_name.setValue(selectEvent.item.name);
    if (selectEvent.item.code) {
      this.form.controls.bank_code.setValue(selectEvent.item.code.toString());
      this.form.controls.bank_code.disable();
    } else {
      this.form.controls.bank_code.setValue("");
      this.form.controls.bank_code.enable();
    }
  }
}
